import React, { useEffect, useState, useRef } from "react";
import SearchSection from "../Components/SearchSection";
import MenuSection from "../Components/MenuSection";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { _get } from "../services/apiClient";
import { useSearch } from "../SearchContext";
import { IoArrowBack } from "react-icons/io5";
import ServiceUnavailable from "../Components/ServiceUnavailable";
import { useRestaurantDetails } from "../context/RestaurantDetailsContext";

function Product({
  handleClick,
  cart,
  setCart,
  handleCartClick,
  restaurantStatus,
}) {
  const [products, setProducts] = useState([]);
  const [offset, setOffset] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [subCategories, setSubCategories] = useState([]);
  const [subCatCounts, setSubCatCounts] = useState({});
  const [isFetching, setIsFetching] = useState(false);
  const { id } = useParams();
  const [isServerSearch, setIsServerSearch] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { restaurantDetails } = useRestaurantDetails();

  const { filtersubCategory } = useSearch();
  const { searchAllCategories, searchData, filter: foodFilter } = useSearch();
  const [hasLoadedAllProducts, setHasLoadedAllProducts] = useState(false);

  // ✅ Detect QR-based entry STRICTLY from URL only
  const queryParams = new URLSearchParams(location.search);
  const urlTableId = queryParams.get("tableId");
  const urlRestaurantId = queryParams.get("restaurantId");

  const tableId = urlTableId;
  const restaurantId = urlRestaurantId;
  const isQrEntry = Boolean(urlTableId && urlRestaurantId);

  useEffect(() => {
    fetchSubCategoryData();
  }, []);

  useEffect(() => {
    goToTop();
    setOffset(1);
    setProducts([]);
    setHasLoadedAllProducts(false);
    fetchData(1, true);
  }, [id, searchData, searchAllCategories, foodFilter, filtersubCategory]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = Math.max(
        window.scrollY || 0,
        document.documentElement.scrollTop || 0,
        document.body.scrollTop || 0
      );

      const scrollHeight = Math.max(
        document.documentElement.scrollHeight,
        document.body.scrollHeight
      );

      const clientHeight = window.innerHeight;

      const threshold = Math.max(800, clientHeight);

      if (scrollTop + clientHeight >= scrollHeight - threshold) {
        if (offset < totalPages && !isFetching) {
          setOffset((prevOffset) => prevOffset + 1);
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [offset, totalPages, isFetching]);

  useEffect(() => {
    if (offset > 1) fetchData(offset, false);
  }, [offset]);

  // Handle deep-linked subcategory loading
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const subcatParam = params.get("subcat");
    if (!subcatParam) return;

    const normalize = (v) => {
      if (!v) return null;
      if (typeof v === "string") return v;
      if (typeof v === "object" && v._id) return v._id;
      if (typeof v === "object" && v.$oid) return v.$oid;
      return null;
    };

    const hasSubcat = products.some(
      (p) => normalize(p.subcategory) === subcatParam
    );

    if (!hasSubcat && offset < totalPages && !isFetching) {
      setOffset((prev) => prev + 1);
    }
  }, [location.search, products, offset, totalPages, isFetching]);

  const goToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const fetchData = async (page, isReset) => {
    try {
      setIsFetching(true);

      let url = "";
      let serverSearch = false;

      const params = new URLSearchParams();
      params.set("page", page);

      if (searchAllCategories && searchData) {
        params.set("search", searchData);
        serverSearch = true;
      } else {
        params.set("category", id);

        if (searchData && !searchAllCategories) {
          params.set("search", searchData);
          serverSearch = true;
        }

        if (filtersubCategory) {
          params.set("subcatId", filtersubCategory);
        }
      }

      // 🥗 Food type filter
      if (foodFilter === "veg" || foodFilter === "nonveg") {
        params.set("foodType", foodFilter);
      }

      // ✅ Add QR params only if true QR entry
      if (isQrEntry) {
        params.set("mode", "dinein");
        params.set("tableId", tableId);
        params.set("restaurantId", restaurantId);
      }

      url = `/products/menu?${params.toString()}`;

      // Show spinner for at least 800ms when paginating for better UX
      if (!isReset) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }

      const response = await _get(url);

      if (isReset) {
        setProducts(response.data.products);
        setTotalCount(response.data.totalCount ?? 0);
        if (response.data.subCatCounts) {
          setSubCatCounts(response.data.subCatCounts);
        }
      } else {
        setProducts((prev) => [...prev, ...response.data.products]);
      }

      setTotalPages(response.data.totalPages);
      setIsServerSearch(serverSearch);
      setIsFetching(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      setIsFetching(false);
    }
  };

  const fetchSubCategoryData = async () => {
    try {
      const response = await _get(`/subcategory`);
      setSubCategories(response.data);
    } catch (error) {
      console.error("Error fetching subcategories:", error);
    }
  };

  if (restaurantDetails && restaurantDetails.amcStatus && restaurantDetails.amcStatus !== "active") {
    return <ServiceUnavailable />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-amber-50">
      <SearchSection
        handleClick={handleClick}
        catId={id}
        subCategories={subCategories}
        cart={cart}
        showBackButton={true}
      />

      <MenuSection
        products={products}
        totalCount={totalCount}
        subCategories={subCategories}
        subCatCounts={subCatCounts}
        handleClick={handleClick}
        handleCartClick={handleCartClick}
        cart={cart}
        setCart={setCart}
        isServerSearch={isServerSearch}
        restaurantStatus={restaurantStatus}
        isFetching={isFetching}
        offset={offset}
        totalPages={totalPages}
      />


    </div>
  );
}

export default Product;